import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getProduct, getRelatedProducts } from "../../actions/productActions";
import { useNavigate, useParams, Link } from "react-router-dom";
import Loader from "../Loader";
import Productgrid from "../../pages/sections/Productgrid";
import axios from "axios";
import Swal from "sweetalert2";
import { addCartItem } from "../../actions/cartActions";

const ProductDetail = () => {
    const { loading, product = {} } = useSelector((state) => state.productState);
    const { isAuthenticated } = useSelector((state) => state.authState);
    const dispatch = useDispatch();
    const { id } = useParams();
    const [currentGoldPrice, setCurrentGoldPrice] = useState(0);
    const [quantity, setQuantity] = useState(1);
    const navigate = useNavigate();
    const userId = useSelector((state) => state.authState.user?._id);
    const itemid = useSelector((state) => state.cartState.items?.product);
    const [relatedProducts, setRelatedProducts] = useState([]);

    // Increase quantity
    const increaseQty = () => {
        if (product.stock > 0 && quantity < product.stock) {
            setQuantity(prev => prev + 1);
        }
    };

    // Decrease quantity
    const decreaseQty = () => {
        if (quantity > 1) {
            setQuantity(prev => prev - 1);
        }
    };

    // Fetch Gold Price & Product Data
    useEffect(() => {
        dispatch(getProduct(id));

        const fetchGoldPrice = async () => {
            try {
                const { data } = await axios.get("/goldprice/admin/show");
                if (data && data.length > 0) {
                    setCurrentGoldPrice(data[0].goldprice);
                }
            } catch (error) {
                console.error("Error fetching gold price:", error);
            }
        };
        fetchGoldPrice();
    }, [dispatch, id]);

    // Fetch Related Products
    useEffect(() => {
        const fetchRelated = async () => {
            if (product && product.category) {
                try {
                    const response = await dispatch(getRelatedProducts(product.category, id));
                    if (response && response.data) {
                        const relatedProductsData = Array.isArray(response.data.products)
                            ? response.data.products
                            : [];
                        setRelatedProducts(relatedProductsData.slice(0, 4));
                    }
                } catch (error) {
                    console.error("Error fetching related products:", error);
                }
            }
        };
        fetchRelated();
    }, [dispatch, id, product.category]);

    const handleAddToCart = () => {
        if (!isAuthenticated) {
            navigate(`/login?redirect=/product/${id}`);
            return;
        }

        const calculatedPrice = (currentGoldPrice / 8) * product.weight + (currentGoldPrice * product.makingrate) / 100;
        
        try {
            if (itemid === id) {
                Swal.fire({
                    icon: 'info',
                    text: "Item already in your cart",
                    confirmButtonColor: '#D4AF37'
                });
                return;
            }
            dispatch(addCartItem(id, currentGoldPrice, quantity, userId));
            Swal.fire({
                icon: 'success',
                title: 'Added to Cart',
                text: `${product.name} has been added.`,
                confirmButtonColor: '#D4AF37'
            });
        } catch (error) {
            console.error(error);
        }
    };

    const displayPrice = currentGoldPrice 
        ? ((currentGoldPrice / 8) * product.weight + (currentGoldPrice * product.makingrate) / 100)
        : 0;

    return (
        <Fragment>
            {loading || !product._id ? (
                <Loader />
            ) : (
                <div className="bg-white min-h-screen">
                    <div className="container mx-auto px-4 py-12 lg:py-24">
                        {/* Breadcrumbs */}
                        <div className="flex items-center space-x-2 text-xs text-gray-400 mb-8 uppercase tracking-widest">
                            <Link to="/" className="hover:text-yellow-600">Home</Link>
                            <span>/</span>
                            <Link to={`/products/${product.category}`} className="hover:text-yellow-600">{product.category}</Link>
                            <span>/</span>
                            <span className="text-gray-900 font-bold">{product.name}</span>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
                            {/* Left Side: Sticky Image Gallery */}
                            <div className="lg:sticky lg:top-32 h-fit">
                                <div className="bg-gray-50 rounded-3xl overflow-hidden aspect-square border border-gray-100 shadow-sm relative group">
                                    <img
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                        src={product.images && product.images.length > 0 ? product.images[0].image : "/images/default_product.png"}
                                        alt={product.name}
                                    />
                                    <div className="absolute top-6 left-6 bg-white/90 backdrop-blur-md px-4 py-2 rounded-full shadow-sm border border-gray-100">
                                        <span className="text-[10px] font-bold tracking-[0.2em] text-gray-900">22K PURITY VERIFIED</span>
                                    </div>
                                </div>
                            </div>

                            {/* Right Side: Product Details */}
                            <div className="flex flex-col">
                                <div className="mb-2 text-xs font-bold tracking-[.3em] text-yellow-600 uppercase">
                                    {product.category} COLLECTION
                                </div>
                                <h1 className="text-4xl lg:text-5xl font-serif font-bold text-gray-900 mb-6 leading-tight">
                                    {product.name}
                                </h1>

                                {/* Price Details */}
                                <div className="mb-8 pb-8 border-b border-gray-100 flex flex-wrap items-end gap-4">
                                    <div className="flex flex-col">
                                        <span className="text-3xl font-bold text-gray-900">
                                            Rs {displayPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                        </span>
                                        <span className="text-[10px] text-gray-400 font-medium tracking-wider uppercase mt-1">
                                            Gold: Rs {currentGoldPrice}/8g + {product.makingrate}% VA
                                        </span>
                                    </div>
                                    <div className="rating-outer mb-1">
                                        <div className="rating-inner" style={{ width: `${(product.ratings / 5) * 100}%` }}></div>
                                    </div>
                                    <span className="text-xs text-gray-400 font-medium pb-1">({product.numOfReviews} Verified Reviews)</span>
                                </div>

                                {/* Description */}
                                <div className="mb-10">
                                    <h3 className="text-xs font-bold tracking-widest text-gray-900 uppercase mb-4">The Inspiration</h3>
                                    <p className="text-gray-600 leading-relaxed font-light text-lg italic">
                                        "{product.description}"
                                    </p>
                                </div>

                                {/* Product Specifications (Aesthetic Table) */}
                                <div className="mb-10 bg-gray-50/50 p-8 rounded-3xl border border-gray-100">
                                    <h3 className="text-xs font-bold tracking-widest text-gray-900 uppercase mb-6">Specifications</h3>
                                    <div className="grid grid-cols-2 gap-y-4 text-sm">
                                        <div className="text-gray-400 font-medium">Metal Purity</div>
                                        <div className="text-gray-900 font-bold text-right">22K Gold (916)</div>
                                        <div className="text-gray-400 font-medium">Approx Weight</div>
                                        <div className="text-gray-900 font-bold text-right">{product.weight} Grams</div>
                                        <div className="text-gray-400 font-medium">Making Rate (VA)</div>
                                        <div className="text-gray-900 font-bold text-right">{product.makingrate}%</div>
                                        <div className="text-gray-400 font-medium">Availability</div>
                                        <div className={`font-bold text-right ${product.stock > 0 ? "text-green-600" : "text-red-500"}`}>
                                            {product.stock > 0 ? "In Stock (Free Shipping)" : "Temporarily Sold Out"}
                                        </div>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <div className="flex items-center bg-gray-100 rounded-2xl p-1 shrink-0 border border-gray-200">
                                        <button 
                                            onClick={decreaseQty}
                                            className="w-12 h-12 flex items-center justify-center text-gray-500 hover:text-gray-900 transition-colors text-xl font-bold"
                                        >
                                            −
                                        </button>
                                        <input 
                                            type="number" 
                                            className="w-12 text-center bg-transparent font-bold text-gray-900 focus:outline-none pointer-events-none"
                                            value={quantity}
                                            readOnly 
                                        />
                                        <button 
                                            onClick={increaseQty}
                                            className="w-12 h-12 flex items-center justify-center text-gray-500 hover:text-gray-900 transition-colors text-xl font-bold"
                                        >
                                            +
                                        </button>
                                    </div>
                                    
                                    <button 
                                        onClick={handleAddToCart}
                                        disabled={product.stock === 0}
                                        className={`flex-1 py-4 px-8 rounded-2xl font-bold text-sm tracking-widest uppercase transition-all duration-300 shadow-lg ${
                                            product.stock > 0 
                                            ? "bg-gray-900 text-white hover:bg-yellow-600 hover:shadow-yellow-600/20 active:scale-95" 
                                            : "bg-gray-200 text-gray-400 cursor-not-allowed"
                                        }`}
                                    >
                                        {product.stock > 0 ? "Add to Shopping Bag" : "Reserved"}
                                    </button>
                                </div>

                                {/* Trust Badges */}
                                <div className="mt-12 grid grid-cols-3 gap-4 border-t border-gray-100 pt-8">
                                    <div className="text-center">
                                        <div className="text-[10px] font-bold text-gray-900 tracking-widest uppercase">Certified</div>
                                        <div className="text-[9px] text-gray-400 uppercase mt-1">Authentic Gold</div>
                                    </div>
                                    <div className="text-center border-x border-gray-100 px-2">
                                        <div className="text-[10px] font-bold text-gray-900 tracking-widest uppercase">Lifetime</div>
                                        <div className="text-[9px] text-gray-400 uppercase mt-1">Buyback Policy</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-[10px] font-bold text-gray-900 tracking-widest uppercase">Secure</div>
                                        <div className="text-[9px] text-gray-400 uppercase mt-1">Insured Shipping</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Related Products Section */}
                        {relatedProducts.length > 0 && (
                            <div className="mt-32 pt-24 border-t border-gray-100">
                                <div className="flex items-center justify-between mb-16">
                                    <div className="flex items-center space-x-6">
                                        <div className="w-16 h-px bg-yellow-600"></div>
                                        <h2 className="text-3xl font-serif font-bold text-gray-900">You May Also Exquisite</h2>
                                    </div>
                                    <Link to={`/products/${product.category}`} className="text-xs font-bold tracking-widest text-yellow-600 border-b-2 border-yellow-600 pb-1 hover:text-gray-900 hover:border-gray-900 transition-all">
                                        EXPLORE ALL
                                    </Link>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12">
                                    {relatedProducts.map((p) => (
                                        <Productgrid key={p._id} product={p} goldPrice={currentGoldPrice} />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </Fragment>
    );
};

export default ProductDetail;
